import React, {useState} from 'react';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useTranslation} from 'react-i18next';

import CommonConfirmDialog from 'crqlar-react-common/dist/components/dialog/CommonConfirmDialog';

import {EmailMode, applyEmailMode, builderReplacesEdits, readEmailMode} from '../../../config/emailModes';
import {NodeEditorV2Props} from '../types';

/** The mode lives in the identity panel because it determines the editor shown beside it. */
const SendEmailModeRadios = ({nodeConfig, setNodeConfig, readOnly}: NodeEditorV2Props) => {
    const {t} = useTranslation();
    const [confirmBuilder, setConfirmBuilder] = useState(false);

    const mode = readEmailMode(nodeConfig);

    const pick = (next: EmailMode) => {
        if (next === 'BUILDER' && builderReplacesEdits(nodeConfig)) {
            setConfirmBuilder(true);
            return;
        }
        setNodeConfig(applyEmailMode(nodeConfig, next));
    };

    const option = (value: EmailMode, label: string, title: string) => (
        <FormControlLabel
            key={value}
            value={value}
            disabled={readOnly}
            sx={{alignItems: 'center', mr: 0, my: 0.5}}
            control={<Radio size={'small'}/>}
            label={
                <Stack direction={'row'} alignItems={'center'} sx={{minWidth: 0}}>
                    <Typography variant={'body1'} sx={{fontWeight: 500}}>{label}</Typography>
                    <Tooltip
                        title={title}
                        placement={'right'}
                        describeChild
                        componentsProps={{tooltip: {sx: {fontSize: '0.85rem'}}}}
                    >
                        <InfoOutlinedIcon
                            tabIndex={0}
                            aria-label={t('Information')}
                            sx={{color: 'info.main', cursor: 'help', ml: 0.5, fontSize: '1.2rem', verticalAlign: 'middle'}}
                        />
                    </Tooltip>
                </Stack>
            }
        />
    );

    return (
        <FormControl disabled={readOnly}>
            <FormLabel sx={{typography: 'body2', mb: 0.5}}>
                {t('How is the email written?')}
            </FormLabel>

            <RadioGroup
                value={mode}
                onChange={event => pick(event.target.value as EmailMode)}
            >
                {option('BUILDER', t('Builder'), t('Design it visually'))}
                {option('MAILEON', t('Maileon template'), t('Pick a transaction type'))}
                {option('CUSTOM', t('Custom HTML'), t('Subject and body, by hand'))}
            </RadioGroup>

            <CommonConfirmDialog
                open={confirmBuilder}
                onClose={() => setConfirmBuilder(false)}
                onConfirm={() => {
                    setConfirmBuilder(false);
                    setNodeConfig(applyEmailMode(nodeConfig, 'BUILDER'));
                }}
                title={t('Back to the builder design?')}
                message={t('Your HTML changes are replaced by the email as it was in the builder.')}
                confirmLabel={t('Use the builder design')}
            />
        </FormControl>
    );
};

export default SendEmailModeRadios;
